import { APP_VERSION, PLATFORM, UNKNOWN } from './config';
import type { DeviceInfo } from './types';

/**
 * The device self-report (SPEC §8).
 *
 * Two of these fields are honestly worse than Android's, and the comments say
 * which and why rather than dressing them up. `model` cannot distinguish an
 * iPhone 12 from an iPhone 16, and `network` cannot tell wifi from 5G. Both are
 * still sent as real, non-blank strings — `"unknown"` is a value, `null` is a
 * gap the backend would have to guess at.
 */
export function deviceInfo(
  userAgent: string = navigator.userAgent,
  appVersion: string = APP_VERSION,
): DeviceInfo {
  return {
    model: deviceModel(userAgent),
    platform: PLATFORM,
    appVersion: appVersion.trim() === '' ? UNKNOWN : appVersion,
    meta: buildMeta(userAgent, appVersion),
  };
}

/**
 * Device class, and nothing finer (SPEC §14 #4).
 *
 * `navigator.userAgent` says `"iPhone"` on every iPhone ever made. There is no
 * API that gives the model, and there is no honest way to infer it — screen size
 * groups several generations together and guesses wrong the moment Apple reuses
 * a resolution. Generic and true beats specific and invented.
 *
 * ### Why this takes a second argument
 *
 * Since iPadOS 13 an iPad in its default "Request Desktop Website" mode sends
 * **the Mac user-agent verbatim** — same `"Macintosh; Intel Mac OS X 10_15_7"`
 * token, no iPad anywhere in the string. No amount of reading the UA separates
 * the two, which is why this used to give up and answer `"iPad or Mac"`.
 *
 * [touchPoints] settles it, and settles it without guessing at a string format:
 * an iPad reports 5 simultaneous touch points, and no Mac Apple has ever shipped
 * reports more than 0. That is a **capability query**, not a scrape — which is
 * the same distinction SPEC §8 draws when it forbids pulling `osVersion` out of
 * `"OS 17_2 like Mac OS X"`. A capability either exists or it does not; a string
 * layout is a convention Apple can change in a point release.
 *
 * The one thing that would break it is a Mac with a touchscreen, which does not
 * exist. If one ever ships, this reports it as an iPad — a wrong answer, but a
 * visible one, and still better than the ambiguous string it replaces.
 */
export function deviceModel(userAgent: string, touchPoints: number = maxTouchPoints()): string {
  const ua = userAgent || '';
  // Order matters. An iPod touch identifies itself as
  // "iPod touch; CPU iPhone OS 16_0 …" — testing for iPhone first would call
  // every iPod an iPhone, and the mistake is invisible in the data because
  // "iPhone" is a perfectly plausible answer.
  if (/iPod/i.test(ua)) return 'iPod';
  // An iPad still in mobile mode says so outright. Kept ahead of the desktop
  // branch so a device that is honest about itself never depends on a touch
  // count that a locked-down configuration profile could suppress.
  if (/iPad/i.test(ua)) return 'iPad';
  if (/iPhone/i.test(ua)) return 'iPhone';
  if (/Macintosh|Mac OS X/i.test(ua)) return touchPoints > 1 ? 'iPad' : 'Mac';
  return UNKNOWN;
}

/**
 * Guarded like every other browser read on this path — see [safeLanguage].
 *
 * Zero on failure, which resolves the desktop-UA branch to `"Mac"`. That is the
 * safer way to be wrong: a field fleet is iPhones, so a Mac in the data reads as
 * "somebody opened the dashboard on their laptop" and gets investigated, while a
 * spurious iPad quietly joins the fleet count.
 */
function maxTouchPoints(): number {
  try {
    return navigator.maxTouchPoints || 0;
  } catch {
    return 0;
  }
}

/**
 * The flexible `meta` object. Every value is a string and every required key is
 * present — the backend stores these as key/value pairs and a missing key is
 * indistinguishable from a key whose value was lost.
 *
 * **`osVersion` is deliberately absent.** It could be scraped out of
 * `"OS 17_2 like Mac OS X"`, but that format is not a contract Apple has made
 * and can change without notice. A best-effort value that silently becomes wrong
 * is worse than no value at all (SPEC §8).
 */
export function buildMeta(userAgent: string, appVersion: string): Record<string, string> {
  return {
    userAgent: nonBlank(userAgent, 'CheckLink-WebClip'),
    appVersion: nonBlank(appVersion, UNKNOWN),
    platform: PLATFORM,
    deviceLanguage: nonBlank(safeLanguage(), UNKNOWN),
    // Whether this is running from the Home Screen icon or from a Safari tab.
    // Worth a key of its own: a field device someone left open in a browser tab
    // behaves differently from a provisioned WebClip, and this is the only
    // signal in the whole payload that would tell anyone that had happened.
    displayMode: standalone() ? 'standalone' : 'browser',
    screen: safeScreen(),
  };
}

/** True when launched from the Home Screen icon rather than a Safari tab. */
export function standalone(): boolean {
  try {
    // `navigator.standalone` is the iOS-specific signal and the reliable one for
    // a WebClip; `display-mode` is the standard that Safari also honours.
    const legacy = (navigator as Navigator & { standalone?: boolean }).standalone;
    if (typeof legacy === 'boolean') return legacy;
    return window.matchMedia?.('(display-mode: standalone)').matches === true;
  } catch {
    return false;
  }
}

/**
 * Every read here is guarded. This runs on the submit path, and an exception
 * from a browser API would cost a batch that had already been measured — the
 * expensive part is done by the time anyone asks what phone it ran on.
 */
function safeLanguage(): string {
  try {
    return navigator.language || '';
  } catch {
    return '';
  }
}

function safeScreen(): string {
  try {
    const ratio = window.devicePixelRatio || 1;
    return `${window.screen.width}x${window.screen.height}@${ratio}`;
  } catch {
    return UNKNOWN;
  }
}

function nonBlank(value: string | null | undefined, fallback: string): string {
  const trimmed = value?.trim();
  return trimmed && trimmed !== '' ? trimmed : fallback;
}
